<template>
  <div class="page-wrapper">
    <div class="page-container">
      <!-- Page Breadcrumbs -->
      <div class="page-header">
        <div class="breadcrumb">
          <span class="crumb-link" @click="$router.push('/')">Asosiy</span>
          <span class="separator">/</span>
          <span class="crumb-current">O'quv reja</span>
        </div>
      </div>

      <!-- Action Bar and Filters -->
      <div class="action-bar-grid">
        <div class="action-buttons">
          <button @click="openCreateModal" class="btn-create">
            <span class="btn-icon">+</span> O'quv reja yaratish
          </button>
          <button @click="openSyncModal" class="btn-sync">
            <span class="btn-icon">🔄</span> HEMIS dan yangilash
          </button>
        </div>

        <div class="filters-row">
          <div class="select-container">
            <SearchableSelect
              v-model="filters.oquvYili"
              :options="oquvYillar"
              placeholder="O'quv yilini tanlang"
              @change="fetchCurriculums"
            />
          </div>

          <div class="select-container">
            <SearchableSelect
              v-model="filters.educationForm"
              :options="educationForms"
              item-value="code"
              item-label="name"
              placeholder="Ta'lim shaklini tanlang"
              @change="fetchCurriculums"
            />
          </div>

          <div class="select-container">
            <SearchableSelect
              v-model="filters.educationType"
              :options="educationTypes"
              item-value="code"
              item-label="name"
              placeholder="Ta'lim turini tanlang"
              @change="fetchCurriculums"
            />
          </div>

          <div class="select-container">
            <SearchableSelect
              v-model="filters.facultyId"
              :options="faculties"
              item-value="id"
              item-label="name"
              placeholder="Fakultetni tanlang"
              @change="fetchCurriculums"
            />
          </div>
        </div>

        <div class="search-filters-row">
          <div class="select-container width-200">
            <SearchableSelect
              v-model="filters.active"
              :options="[{code: 'true', name: 'Faol'}, {code: 'false', name: 'Faol emas'}]"
              item-value="code"
              item-label="name"
              placeholder="Holati"
              @change="fetchCurriculums"
            />
          </div>

          <div class="search-container">
            <span class="search-icon">🔍</span>
            <input
              v-model="filters.search"
              @input="debounceSearch"
              type="text"
              placeholder="O'quv reja Nomi bo'yicha qidirish"
              class="search-input"
            />
          </div>
        </div>
      </div>

      <!-- Curriculum Table -->
      <div class="table-card">
        <div v-if="loading" class="loading-overlay">
          <div class="spinner"></div>
          <span>Yuklanmoqda...</span>
        </div>

        <table class="main-table">
          <thead>
            <tr>
              <th class="w-8 text-center">#</th>
              <th>Nomi</th>
              <th>Fakultet</th>
              <th class="w-120">Actions</th>
              <th class="w-24 text-center">Tasdiq</th>
              <th class="w-24 text-center">Faol</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loading && curriculums.length === 0">
              <td colspan="6" class="py-10 text-center text-gray-400">Ma'lumot topilmadi. Tizimni yangilash yoki qo'shish tugmalaridan foydalaning.</td>
            </tr>
            <tr v-for="(row, index) in curriculums" :key="row._id" class="data-row">
              <td class="text-center text-gray-500">{{ (currentPage - 1) * limit + index + 1 }}</td>
              <td class="curriculum-name-col">
                <div class="curr-name">{{ row.name }}</div>
                <div class="curr-subtext" v-if="row.specialty">{{ row.specialty.name }}</div>
              </td>
              <td>
                <div class="fac-name" v-if="row.faculty">{{ row.faculty.name }}</div>
                <div class="fac-subtext">
                  <span class="badge-type" v-if="row.educationType">{{ row.educationType.name }}</span>
                  <span class="badge-form" v-if="row.educationForm">{{ row.educationForm.name }}</span>
                  <span class="badge-system" v-if="row.markingSystem">{{ row.markingSystem.name || row.markingSystem }}</span>
                </div>
              </td>
              <td>
                <div class="action-badges">
                  <button @click="showDetail(row, 'Semestr')" class="badge-btn">Semestr</button>
                  <button @click="showDetail(row, 'Xafta')" class="badge-btn">Xafta</button>
                  <button @click="showDetail(row, 'Fanlar bloki')" class="badge-btn">Fanlar bloki</button>
                  <button @click="showDetail(row, 'Fanlar')" class="badge-btn">Fanlar</button>
                  <button @click="showDetail(row, 'Mavzular')" class="badge-btn">Mavzular</button>
                </div>
              </td>
              <td class="text-center">
                <label class="switch">
                  <input type="checkbox" :checked="row.tasdiq" @change="toggleState(row, 'tasdiq')">
                  <span class="slider round"></span>
                </label>
              </td>
              <td class="text-center">
                <label class="switch">
                  <input type="checkbox" :checked="row.active" @change="toggleState(row, 'active')">
                  <span class="slider round"></span>
                </label>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="table-footer" v-if="totalCount > 0">
          <div class="pagination-buttons">
            <button @click="changePage(1)" :disabled="currentPage === 1" class="page-link">First</button>
            <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="page-link">«</button>
            <button
              v-for="p in visiblePages"
              :key="p"
              @click="changePage(p)"
              :class="['page-link', { active: currentPage === p }]"
            >
              {{ p }}
            </button>
            <button @click="changePage(currentPage + 1)" :disabled="currentPage >= pageCount" class="page-link">»</button>
            <button @click="changePage(pageCount)" :disabled="currentPage >= pageCount" class="page-link">Last</button>
          </div>
          <div class="total-info">{{ totalInfoText }}</div>
        </div>
      </div>

      <!-- Sync Modal -->
      <transition name="fade">
        <div v-if="showSyncModal" class="modal-backdrop" @click.self="closeSyncModal">
          <div class="modal-content">
            <div class="modal-header">
              <h2>HEMIS dan o'quv rejalarni yuklash</h2>
              <button @click="closeSyncModal" class="close-btn">&times;</button>
            </div>
            <div class="modal-body">
              <p class="sync-desc">
                HEMIS axborot tizimidan barcha o'quv rejalarni yuklash va ma'lumotlar bazasini sinxronlashtirish uchun "Sinxronlash" tugmasini bosing.
              </p>
              <div class="form-group mt-4" style="display:none;">
                <label>HEMIS API Token</label>
                <input type="text" v-model="token" class="modal-input" placeholder="Token backendda .env orqali olinadi" />
              </div>
              <div class="modal-footer mt-6">
                <button @click="submitSync" :disabled="syncLoading" class="btn-save">
                  <span v-if="!syncLoading" class="save-icon">🔄</span>
                  <div v-else class="spinner-inline"></div>
                  {{ syncLoading ? "Sinxronlashtirilmoqda..." : "Sinxronlash" }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Create Modal -->
      <transition name="fade">
        <div v-if="showCreateModal" class="modal-backdrop" @click.self="closeCreateModal">
          <div class="modal-content">
            <div class="modal-header">
              <h2>Yangi o'quv reja yaratish</h2>
              <button @click="closeCreateModal" class="close-btn">&times;</button>
            </div>
            <form @submit.prevent="submitCreate" class="modal-body">
              <div class="form-group">
                <label>O'quv reja nomi *</label>
                <input type="text" v-model="createForm.name" required placeholder="Masalan: TVM (2024-2025) Transport vositalari..." class="modal-input" />
              </div>

              <div class="form-row">
                <div class="form-group flex-1">
                  <label>O'quv yili *</label>
                  <input type="text" v-model="createForm.oquvYili" required placeholder="Masalan: 2024-2025" class="modal-input" />
                </div>
                <div class="form-group flex-1">
                  <label>Baholash tizimi</label>
                  <input type="text" v-model="createForm.markingSystem" placeholder="Masalan: 5 baholik baholash tizimi" class="modal-input" />
                </div>
              </div>

              <div class="form-group">
                <label>Fakultet nomi *</label>
                <input type="text" v-model="createForm.facultyName" required placeholder="Masalan: Mexanika va konchilik ishi" class="modal-input" />
              </div>

              <div class="form-group">
                <label>Mutaxassislik (Yo'nalish) nomi *</label>
                <input type="text" v-model="createForm.specialtyName" required placeholder="Masalan: Transport vositalari muhandisligi" class="modal-input" />
              </div>

              <div class="form-row">
                <div class="form-group flex-1">
                  <label>Ta'lim shakli *</label>
                  <select v-model="createForm.educationForm" required class="modal-select">
                    <option value="11">Kunduzgi</option>
                    <option value="12">Sirtqi</option>
                    <option value="13">Kechki</option>
                    <option value="14">Masofaviy</option>
                  </select>
                </div>
                <div class="form-group flex-1">
                  <label>Ta'lim turi *</label>
                  <select v-model="createForm.educationType" required class="modal-select">
                    <option value="11">Bakalavr</option>
                    <option value="12">Magistr</option>
                  </select>
                </div>
              </div>

              <div class="modal-footer">
                <button type="submit" :disabled="createLoading" class="btn-save">
                  <span class="save-icon">✓</span> Yaratish
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>

      <!-- Details Modal -->
      <transition name="fade">
        <div v-if="showDetailModal" class="modal-backdrop" @click.self="closeDetailModal">
          <div class="modal-content width-800">
            <div class="modal-header">
              <h2>{{ detailTitle }}</h2>
              <button @click="closeDetailModal" class="close-btn">&times;</button>
            </div>
            <div class="modal-body max-height-600">
              <div v-if="detailType === 'Semestr'">
                <p>O'quv rejadagi semestrlar ro'yxati (Hemis ma'lumotlari asosida):</p>
                <div v-if="semestersLoading" class="text-center py-6">
                  <div class="spinner"></div>
                  <span class="text-gray-400">Semestrlar yuklanmoqda...</span>
                </div>
                <div class="table-responsive mt-3" v-else-if="selectedCurriculumSemesters && selectedCurriculumSemesters.length > 0">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th class="w-8 text-center">Kod</th>
                        <th>Nomi</th>
                        <th>Kurs</th>
                        <th>O'quv yili</th>
                        <th>Muddat</th>
                        <th class="w-16 text-center">Tasdiq</th>
                        <th class="w-16 text-center">Faol</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sem in selectedCurriculumSemesters" :key="sem.id">
                        <td class="text-center text-gray-500 font-bold">{{ sem.code }}</td>
                        <td class="font-semibold text-slate-800">{{ sem.name }}</td>
                        <td>{{ sem.level }}</td>
                        <td>{{ sem.educationYear }}</td>
                        <td>{{ sem.dateRange }}</td>
                        <td class="text-center">
                          <label class="switch-static">
                            <span :class="['bullet-active', { active: sem.tasdiq }]"></span>
                            {{ sem.tasdiq ? 'Tasdiq' : 'Kutilmoqda' }}
                          </label>
                        </td>
                        <td class="text-center">
                          <label class="switch-static">
                            <span :class="['bullet-active', { active: sem.active }]"></span>
                            {{ sem.active ? 'Faol' : 'Nofaol' }}
                          </label>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center py-6 text-gray-400">
                  Ushbu o'quv reja uchun semestrlar ro'yxati kiritilmagan yoki sinxronlashtirilmagan.
                </div>
              </div>
              <div v-else-if="detailType === 'Xafta'">
                <p>O'quv rejadagi haftalar taqsimoti (Hemis ma'lumotlari asosida):</p>
                <div v-if="weeksLoading" class="text-center py-6">
                  <div class="spinner"></div>
                  <span class="text-gray-400">Haftalar yuklanmoqda...</span>
                </div>
                <div class="table-responsive mt-3" v-else-if="selectedCurriculumWeeks && selectedCurriculumWeeks.length > 0">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th class="w-8 text-center">№</th>
                        <th>Xafta</th>
                        <th>Kurs</th>
                        <th>Semestr</th>
                        <th>Hafta turi</th>
                        <th class="w-16 text-center">Faol</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(week, index) in selectedCurriculumWeeks" :key="week.id || index">
                        <td class="text-center text-gray-500 font-bold">{{ index + 1 }}</td>
                        <td class="font-medium text-slate-800">{{ week.dateRange }}</td>
                        <td>{{ week.level }}</td>
                        <td>{{ week.semester }}</td>
                        <td>
                          <span :class="['badge-week-type', getWeekTypeClass(week.weekType)]">
                            {{ week.weekType }}
                          </span>
                        </td>
                        <td class="text-center">
                          <label class="switch-static">
                            <span :class="['bullet-active', { active: week.active }]"></span>
                            {{ week.active ? 'Faol' : 'Nofaol' }}
                          </label>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center py-6 text-gray-400">
                  Ushbu o'quv reja uchun haftalar taqsimoti kiritilmagan yoki sinxronlashtirilmagan.
                </div>
              </div>
              <div v-else-if="detailType === 'Fanlar bloki'">
                <p>O'quv rejadagi fanlar bloklari taqsimoti (Hemis ma'lumotlari asosida):</p>
                <div v-if="blocksLoading" class="text-center py-6">
                  <div class="spinner"></div>
                  <span class="text-gray-400">Fanlar bloklari yuklanmoqda...</span>
                </div>
                <div class="detail-list mt-3" v-else-if="selectedCurriculumBlocks && selectedCurriculumBlocks.length > 0">
                  <div class="detail-item-complex" v-for="block in selectedCurriculumBlocks" :key="block.code || block.name">
                    <div class="complex-title">
                      <span class="block-code" v-if="block.code">{{ block.code }}. </span>
                      <span class="block-name">{{ block.name }}</span>
                    </div>
                    <div class="complex-desc" v-if="block.totalCredits !== undefined || block.subjectCount !== undefined">
                      Kreditlar soni: {{ block.totalCredits }} | Fanlar: {{ block.subjectCount }} ta
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-6 text-gray-400">
                  Ushbu o'quv reja uchun fanlar bloklari kiritilmagan yoki sinxronlashtirilmagan.
                </div>
              </div>
              <div v-else-if="detailType === 'Fanlar'">
                <p>O'quv rejadagi fanlar ro'yxati — semestrlar bo'yicha:</p>
                <div v-if="subjectsLoading" class="text-center py-6">
                  <div class="spinner"></div>
                  <span class="text-gray-400">Fanlar yuklanmoqda...</span>
                </div>
                <div v-else-if="subjectsBySemester.length > 0" class="subjects-by-semester mt-3">
                  <div
                    class="semester-group"
                    v-for="group in subjectsBySemester"
                    :key="group.semesterCode"
                  >
                    <div class="semester-group-header">
                      <span class="sem-label">{{ group.semesterName }}</span>
                      <span class="sem-count">{{ group.subjects.length }} ta fan</span>
                    </div>
                    <div class="semester-subjects">
                      <div
                        class="detail-item"
                        v-for="(sub, idx) in group.subjects"
                        :key="sub.id || idx"
                      >
                        <div class="subject-info">
                          <span class="subject-num">{{ idx + 1 }}.</span>
                          <span class="subject-name">{{ sub.name }}</span>
                          <span class="subject-code" v-if="sub.code">[{{ sub.code }}]</span>
                        </div>
                        <span class="badge-credit">{{ sub.credit }} kredit</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-6 text-gray-400">
                  Ushbu o'quv reja uchun fanlar ro'yxati kiritilmagan yoki sinxronlashtirilmagan.
                </div>
              </div>
              <div v-else-if="detailType === 'Mavzular'">
                <p>O'quv rejadagi fanlarga oid mavzular taqsimoti:</p>
                <div class="text-center py-6 text-gray-400">
                  Hozircha fanlarga oid mavzular ro'yxati mavjud emas.
                </div>
              </div>
            </div>
            <div class="modal-footer bg-gray-50">
              <button @click="closeDetailModal" class="btn-cancel">Yopish</button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import SearchableSelect from "../components/SearchableSelect.vue";

const API = process.env.VUE_APP_API_URL;

export default {
  name: "IshchiOquvRejalar",
  components: {
    SearchableSelect
  },
  data() {
    return {
      token: "",
      loading: false,
      curriculums: [],
      oquvYillar: ["2029-2030", "2028-2029", "2027-2028", "2026-2027", "2025-2026", "2024-2025", "2023-2024", "2022-2023", "2021-2022"],
      educationForms: [
        { code: "11", name: "Kunduzgi" },
        { code: "12", name: "Sirtqi" },
        { code: "13", name: "Kechki" },
        { code: "14", name: "Masofaviy" }
      ],
      educationTypes: [
        { code: "11", name: "Bakalavr" },
        { code: "12", name: "Magistr" }
      ],
      faculties: [],
      filters: {
        oquvYili: "",
        educationForm: "",
        educationType: "",
        facultyId: "",
        active: "",
        search: ""
      },
      currentPage: 1,
      limit: 50,
      totalCount: 0,
      pageCount: 1,
      showCreateModal: false,
      showSyncModal: false,
      syncLoading: false,
      createLoading: false,
      createForm: {
        name: "",
        oquvYili: "",
        facultyName: "",
        specialtyName: "",
        educationForm: "11",
        educationType: "11",
        markingSystem: "5 baholik baholash tizimi"
      },
      showDetailModal: false,
      detailType: "",
      detailTitle: "",
      selectedCurriculum: null,
      selectedCurriculumSubjects: [],
      selectedCurriculumBlocks: [],
      selectedCurriculumWeeks: [],
      selectedCurriculumSemesters: [],
      subjectsLoading: false,
      blocksLoading: false,
      weeksLoading: false,
      semestersLoading: false,
      searchTimeout: null
    };
  },
  computed: {
    totalInfoText() {
      if (this.totalCount === 0) return "0-0 / Jami 0 ta";
      const start = (this.currentPage - 1) * this.limit + 1;
      const end = Math.min(this.currentPage * this.limit, this.totalCount);
      return `${start}-${end} / Jami ${this.totalCount} ta`;
    },
    visiblePages() {
      const range = [];
      const maxVisible = 10;
      let start = Math.max(1, this.currentPage - 5);
      let end = Math.min(this.pageCount, start + maxVisible - 1);
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    },
    subjectsBySemester() {
      if (!this.selectedCurriculumSubjects || this.selectedCurriculumSubjects.length === 0) return [];
      const groups = {};
      this.selectedCurriculumSubjects.forEach((sub) => {
        const code = sub.semester && sub.semester.code ? String(sub.semester.code) : "0";
        const name = sub.semester && sub.semester.name ? sub.semester.name : ("Semestr " + code);
        if (!groups[code]) {
          groups[code] = { semesterCode: code, semesterName: name, subjects: [] };
        }
        groups[code].subjects.push(sub);
      });
      return Object.values(groups).sort((a, b) => Number(a.semesterCode) - Number(b.semesterCode));
    }
  },
  methods: {
    async fetchFilters() {
      try {
        const res = await axios.get(`${API}/curriculums/filters`);
        if (res.data.success) {
          const d = res.data.data;
          if (d.oquvYillar && d.oquvYillar.length > 0) {
            const combined = [...new Set([...this.oquvYillar, ...d.oquvYillar])];
            this.oquvYillar = combined.sort((a, b) => b.localeCompare(a));
          }
          if (d.faculties && d.faculties.length > 0) {
            // Group unique faculties by id
            const uFac = {};
            d.faculties.forEach(f => {
              if (f && f.id) uFac[f.id] = f;
            });
            this.faculties = Object.values(uFac);
          }
        }
      } catch (err) {
        console.error("Filters loading error:", err);
      }
    },
    async fetchCurriculums() {
      this.loading = true;
      try {
        const res = await axios.get(`${API}/curriculums`, {
          params: {
            oquvYili: this.filters.oquvYili,
            facultyId: this.filters.facultyId,
            educationForm: this.filters.educationForm,
            educationType: this.filters.educationType,
            active: this.filters.active,
            search: this.filters.search,
            page: this.currentPage,
            limit: this.limit
          }
        });
        if (res.data.success) {
          this.curriculums = res.data.data || [];
          this.totalCount = res.data.total || 0;
          this.pageCount = res.data.pageCount || 1;
        }
      } catch (err) {
        console.error("Curriculums loading error:", err);
      } finally {
        this.loading = false;
      }
    },
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchCurriculums();
      }, 400);
    },
    changePage(page) {
      if (page < 1 || page > this.pageCount) return;
      this.currentPage = page;
      this.fetchCurriculums();
    },
    openSyncModal() {
      this.showSyncModal = true;
    },
    closeSyncModal() {
      this.showSyncModal = false;
    },
    async submitSync() {
      this.syncLoading = true;
      try {
        const res = await axios.post(`${API}/curriculums/sync`, {
          oquvYili: this.filters.oquvYili || undefined,
          facultyId: this.filters.facultyId || undefined,
          educationForm: this.filters.educationForm || undefined,
          educationType: this.filters.educationType || undefined
        });
        if (res.data.success) {
          alert(`Sinxronlash muvaffaqiyatli yakunlandi! Jami o'quv rejalar: ${res.data.count}`);
          this.closeSyncModal();
          this.currentPage = 1;
          await this.fetchFilters();
          await this.fetchCurriculums();
        }
      } catch (err) {
        console.error(err);
        alert("Sinxronlashda xatolik yuz berdi. Iltimos tokeningizni tekshiring.");
      } finally {
        this.syncLoading = false;
      }
    },
    openCreateModal() {
      this.showCreateModal = true;
      this.createForm = {
        name: "",
        oquvYili: "2024-2025",
        facultyName: "",
        specialtyName: "",
        educationForm: "11",
        educationType: "11",
        markingSystem: "5 baholik baholash tizimi"
      };
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    async submitCreate() {
      this.createLoading = true;
      try {
        const selectedFormText = this.educationForms.find(f => f.code === this.createForm.educationForm)?.name || "Kunduzgi";
        const selectedTypeText = this.educationTypes.find(t => t.code === this.createForm.educationType)?.name || "Bakalavr";

        const payload = {
          name: this.createForm.name,
          oquvYili: this.createForm.oquvYili,
          markingSystem: this.createForm.markingSystem,
          active: true,
          tasdiq: false,
          faculty: {
            id: Math.floor(Math.random() * 1000) + 100,
            name: this.createForm.facultyName,
            code: String(Math.floor(Math.random() * 100) + 10)
          },
          specialty: {
            id: Math.floor(Math.random() * 1000) + 100,
            name: this.createForm.specialtyName,
            code: String(Math.floor(Math.random() * 1000) + 100)
          },
          educationForm: {
            code: this.createForm.educationForm,
            name: selectedFormText
          },
          educationType: {
            code: this.createForm.educationType,
            name: selectedTypeText
          }
        };

        const res = await axios.post(`${API}/curriculums`, payload);
        if (res.data.success) {
          alert("O'quv reja muvaffaqiyatli yaratildi!");
          this.closeCreateModal();
          this.currentPage = 1;
          await this.fetchFilters();
          await this.fetchCurriculums();
        }
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.error || "O'quv reja yaratishda xatolik yuz berdi.");
      } finally {
        this.createLoading = false;
      }
    },
    async toggleState(row, field) {
      const prevVal = row[field];
      row[field] = !row[field];
      try {
        await axios.patch(`${API}/curriculums/${row._id}`, {
          [field]: row[field]
        });
      } catch (err) {
        console.error(err);
        row[field] = prevVal;
        alert("O'zgarishni saqlashda xatolik yuz berdi.");
      }
    },
    async showDetail(row, type) {
      this.selectedCurriculum = row;
      this.detailType = type;
      this.detailTitle = `${row.name} — ${type} tafsilotlari`;
      this.selectedCurriculumSubjects = [];
      this.selectedCurriculumBlocks = [];
      this.selectedCurriculumWeeks = [];
      this.selectedCurriculumSemesters = [];
      this.subjectsLoading = false;
      this.blocksLoading = false;
      this.weeksLoading = false;
      this.semestersLoading = false;
      this.showDetailModal = true;

      if (type === "Fanlar") {
        this.subjectsLoading = true;
        try {
          const res = await axios.get(`${API}/curriculums/${row.curriculumId}/subjects`, {
            params: { token: this.token }
          });
          if (res.data.success) {
            this.selectedCurriculumSubjects = res.data.data || [];
          }
        } catch (err) {
          console.error("Error fetching curriculum subjects:", err);
        } finally {
          this.subjectsLoading = false;
        }
      } else if (type === "Fanlar bloki") {
        this.blocksLoading = true;
        try {
          const res = await axios.get(`${API}/curriculums/${row.curriculumId}/blocks`, {
            params: { token: this.token }
          });
          if (res.data.success) {
            this.selectedCurriculumBlocks = res.data.data || [];
          }
        } catch (err) {
          console.error("Error fetching curriculum blocks:", err);
        } finally {
          this.blocksLoading = false;
        }
      } else if (type === "Xafta") {
        this.weeksLoading = true;
        try {
          const res = await axios.get(`${API}/curriculums/${row.curriculumId}/weeks`, {
            params: { token: this.token }
          });
          if (res.data.success) {
            this.selectedCurriculumWeeks = res.data.data || [];
          }
        } catch (err) {
          console.error("Error fetching curriculum weeks:", err);
        } finally {
          this.weeksLoading = false;
        }
      } else if (type === "Semestr") {
        this.semestersLoading = true;
        try {
          const res = await axios.get(`${API}/curriculums/${row.curriculumId}/semesters`, {
            params: { token: this.token }
          });
          if (res.data.success) {
            this.selectedCurriculumSemesters = res.data.data || [];
          }
        } catch (err) {
          console.error("Error fetching curriculum semesters:", err);
        } finally {
          this.semestersLoading = false;
        }
      }
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedCurriculum = null;
      this.selectedCurriculumSubjects = [];
      this.selectedCurriculumBlocks = [];
      this.selectedCurriculumWeeks = [];
      this.selectedCurriculumSemesters = [];
      this.detailType = "";
      this.detailTitle = "";
    },
    getWeekTypeClass(weekType) {
      if (!weekType) return "";
      const wt = weekType.toLowerCase();
      if (wt.includes("nazariy")) return "type-theoretical";
      if (wt.includes("attestatsiya") || wt.includes("imtihon")) return "type-exam";
      if (wt.includes("amaliyot") || wt.includes("malaka")) return "type-practice";
      if (wt.includes("ta'til") || wt.includes("tatil")) return "type-holiday";
      return "";
    }
  },
  mounted() {
    this.fetchFilters();
    this.fetchCurriculums();
  }
};
</script>

<style scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f6f7fb;
  font-family: 'Inter', sans-serif;
}

.page-container {
  flex: 1;
  padding: 24px;
}

/* Breadcrumb Styling */
.page-header {
  padding-bottom: 18px;
}

.breadcrumb {
  font-size: 14px;
  font-weight: 600;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.3px;
}

.crumb-link {
  color: #88929c;
  cursor: pointer;
  transition: color 0.2s;
}

.crumb-link:hover {
  color: #3b82f6;
}

.separator {
  color: #d1d5db;
  font-weight: 400;
}

.crumb-current {
  color: #1e293b;
}

/* Action Bar Grid Layout */
.action-bar-grid {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 25px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-create {
  background-color: #10b981; /* Emerald 500 */
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-create:hover {
  background-color: #059669;
  transform: translateY(-1px);
}

.btn-sync {
  background-color: #3b4461;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-sync:hover {
  background-color: #2d344d;
  transform: translateY(-1px);
}

/* Filter controls row */
.filters-row, .search-filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.select-container {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.select-container.width-200 {
  flex: 0 0 200px;
  min-width: 200px;
}

.filter-select {
  width: 100%;
  padding: 11px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #334155;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}

.filter-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.select-arrow {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 0; height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #64748b;
  pointer-events: none;
}

/* Search bar styling */
.search-container {
  position: relative;
  flex: 1;
  min-width: 300px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 14px;
}

.search-input {
  width: 100%;
  padding: 11px 16px 11px 42px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #334155;
  outline: none;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Table Card Layout */
.table-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  overflow-x: auto;
  position: relative;
  border: 1px solid #f1f5f9;
}

.main-table {
  width: 100%;
  border-collapse: collapse;
}

.main-table th {
  background-color: #f8fafc;
  padding: 16px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
}

.main-table th.text-center {
  text-align: center;
}

.data-row {
  border-bottom: 1px solid #f8fafc;
  transition: background-color 0.2s;
}

.data-row:hover {
  background-color: #f8fafc;
}

.data-row td {
  padding: 16px;
  font-size: 13.5px;
  color: #334155;
  vertical-align: middle;
}

.data-row td.text-center {
  text-align: center;
}

/* Column custom classes */
.curriculum-name-col {
  max-width: 400px;
}

.curr-name {
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  font-size: 14px;
}

.curr-subtext {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.fac-name {
  font-weight: 500;
  color: #475569;
}

.fac-subtext {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.fac-subtext span {
  font-size: 10.5px;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.badge-type {
  background-color: #eff6ff;
  color: #1e40af;
}

.badge-form {
  background-color: #ecfdf5;
  color: #065f46;
}

.badge-system {
  background-color: #fef3c7;
  color: #92400e;
}

/* Action Badges in Table row */
.action-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 450px;
}

.badge-btn {
  border: none;
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;
  background-color: #00a65a;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.badge-btn:hover {
  background-color: #008d4c;
}

/* Toggle Switches */
.switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
}

.switch input { opacity: 0; width: 0; height: 0; }

.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #cbd5e1;
  transition: .3s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px; width: 18px;
  left: 3px; bottom: 3px;
  background-color: white;
  transition: .3s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

input:checked + .slider { background-color: #10b981; }
input:checked + .slider:before { transform: translateX(18px); }

.slider.round { border-radius: 24px; }
.slider.round:before { border-radius: 50%; }

/* Pagination Styling */
.table-footer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px;
  background-color: #ffffff;
  border-top: 1px solid #e2e8f0;
}

.pagination-buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.page-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  min-width: 32px;
  font-size: 13px;
  color: #0f172a;
  background-color: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  transition: all 0.15s;
}

.page-link:hover:not(:disabled) {
  background-color: #f1f5f9;
  border-color: #94a3b8;
}

.page-link.active {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.page-link:disabled {
  color: #94a3b8;
  border-color: #e2e8f0;
  cursor: not-allowed;
  opacity: 0.6;
}

.total-info {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
  margin-top: 4px;
}

/* Modal Styling */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4); /* Slate 900 translucent */
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  width: 500px;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid #f1f5f9;
}

.modal-content.width-600 {
  width: 600px;
}

.modal-content.width-800 {
  width: 800px;
  max-width: 95vw;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8fafc;
}

.modal-header h2 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.close-btn {
  font-size: 26px;
  color: #94a3b8;
  border: none;
  background: none;
  cursor: pointer;
  transition: color 0.15s;
}

.close-btn:hover {
  color: #475569;
}

.modal-body {
  padding: 24px;
}

.modal-body.max-height-400 {
  max-height: 400px;
  overflow-y: auto;
}

.modal-body.max-height-600 {
  max-height: 600px;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}

.modal-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13.5px;
  color: #1e293b;
  outline: none;
  transition: all 0.2s;
}

.modal-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-row {
  display: flex;
  gap: 16px;
}

.flex-1 {
  flex: 1;
}

.modal-select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13.5px;
  color: #1e293b;
  background: white;
  outline: none;
  cursor: pointer;
}

.modal-select:focus {
  border-color: #3b82f6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn-save {
  background-color: #10b981;
  color: white;
  padding: 11px 24px;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(16, 185, 129, 0.15);
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  background-color: #059669;
  transform: translateY(-1px);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background-color: white;
  border: 1px solid #e2e8f0;
  color: #475569;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background-color: #f8fafc;
  color: #0f172a;
}

/* Detail Modal Styles */
.detail-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
  font-size: 13.5px;
  font-weight: 500;
}

.badge-status-green {
  background-color: #d1fae5;
  color: #065f46;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.badge-credit {
  background-color: #fef3c7;
  color: #92400e;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

/* Week Types Badges */
.badge-week-type {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-block;
  white-space: nowrap;
}

.type-theoretical {
  background-color: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.type-exam {
  background-color: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.type-practice {
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.type-holiday {
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fef3c7;
}

/* Static switch / active bullets */
.switch-static {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background-color: #f8fafc;
  padding: 3px 8px;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
}

.bullet-active {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #cbd5e1;
}

.bullet-active.active {
  background-color: #10b981;
  box-shadow: 0 0 4px rgba(16, 185, 129, 0.6);
}

.detail-item-complex {
  padding: 14px 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.complex-title {
  font-weight: 600;
  color: #1e293b;
  font-size: 13.5px;
}

.complex-desc {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 4px;
}

.block-code {
  color: #3b82f6;
  font-weight: 700;
  margin-right: 4px;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table th {
  background-color: #f8fafc;
  padding: 10px;
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  border-bottom: 1px solid #f1f5f9;
}

.detail-table td {
  padding: 10px;
  font-size: 12.5px;
  border-bottom: 1px solid #f8fafc;
}

/* Utilities */
.spinner, .spinner-inline {
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  margin-bottom: 10px;
}

.spinner-inline {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
}

.sync-desc {
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.5;
}

.bg-gray-50 {
  background-color: #f8fafc;
}

.mt-3 { margin-top: 12px; }
.mt-4 { margin-top: 16px; }
.mt-6 { margin-top: 24px; }
.py-6 { padding-top: 24px; padding-bottom: 24px; }
.py-10 { padding-top: 40px; padding-bottom: 40px; }
.text-center { text-align: center; }
.text-gray-400 { color: #94a3b8; }
.text-gray-500 { color: #64748b; }
.font-bold { font-weight: 700; }

/* Subject Info in Fanlar modal */
.subject-info {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
}

.subject-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
}

.subject-code {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.subject-semester {
  font-size: 10.5px;
  background-color: #eff6ff;
  color: #1e40af;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.badge-credit {
  font-size: 11px;
  background-color: #ecfdf5;
  color: #065f46;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Semester-grouped subjects */
.subjects-by-semester {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.semester-group {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.semester-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
  color: #fff;
}

.sem-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.sem-count {
  font-size: 11px;
  background: rgba(255,255,255,0.2);
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}

.semester-subjects {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.semester-subjects .detail-item {
  border-bottom: 1px solid #f1f5f9;
  border-radius: 0;
  padding: 7px 14px;
}

.semester-subjects .detail-item:last-child {
  border-bottom: none;
}

.subject-num {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
  min-width: 20px;
}

</style>
